import { type ReactElement, useEffect, useState, useContext, type SyntheticEvent } from 'react'
// import { type MetaTransactionData } from '@safe-global/safe-core-sdk-types'
import { Box, Grid, Button, Input, Slider, styled } from '@mui/material'
import { Body2White } from '@/components/typography'
import useDesktop from '@/hooks/useDesktop'
import { type Inheritor } from '@/store/setUpInheritorsSlice'
import { ModalButtonContext } from '@/components/common/ModalButton'
import { SendProcessingContext } from '@/components/inheritors/SendProcessing'
import { PendingStatus } from '@/store/pendingTxsSlice'
import { encodeAddInheritorFunc, encodeChangeInheritorFunc } from '@/utils/inherit-manager'
import useChainId from '@/hooks/useChainId'
import { useAppDispatch } from '@/store'
import { upsertAddressBookEntry } from '@/store/addressBookV2Slice'
import useAddressBook from '@/hooks/useAddressBook'
import TextInputComponent from '@/components/common/TextInputComponent'
import { validateAddress } from '@/utils/validation'
import { sameAddress } from '@/utils/addresses'
import useSafeAddress from '@/hooks/useSafeAddress'
import useWallet from '@/hooks/wallets/useWallet'
// import { trackEvent, INHERIT_EVENTS } from '@/services/analytics'
// import FeeSection from '@/components/common/FeeSection'

const PrettoSlider = styled(Slider)({
  color: '#5D5DFF',
  height: 4,
  '& .MuiSlider-track': {
    border: 'none',
  },
  '& .MuiSlider-thumb': {
    height: 24,
    width: 12,
    borderRadius: '2px',

    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&::before': {
      display: 'none',
    },
  },
})

const EditInheritorComponent = ({
  index,
  inheritors,
  maxPercentage,
  onSendSuccess,
}: {
  index: number
  inheritors: Inheritor[]
  maxPercentage: number
  onSendSuccess: () => void
}): ReactElement => {
  const isDesktop = useDesktop()
  const dispatch = useAppDispatch()
  const addressBook = useAddressBook()
  const safeAddress = useSafeAddress()
  const wallet = useWallet()
  const isCoinbase = wallet?.label && wallet.label.toLowerCase().indexOf('coinbase') !== -1 && !isDesktop ? true : false

  const { setModalOpen } = useContext(ModalButtonContext)
  const { isSending, sendTx, sendError, txStatus } = useContext(SendProcessingContext)

  const inheritor = inheritors[index]

  //   const [maxPercentage, setMaxPercentage] = useState<number>(100)
  const [percentage, setPercentage] = useState<number>(inheritor?.percentage || 0)
  const [warning, setWarning] = useState<boolean>(true)

  const [address, setAddress] = useState<string>(inheritor?.address || '')
  const [addressError, setAddressError] = useState<string | undefined>(undefined)

  const [name, setName] = useState<string>(inheritor?.name || '')
  const [nameError, setNameError] = useState<string | undefined>(undefined)

  const [email, setEmail] = useState<string>(inheritor?.email || '')
  const [emailError, setEmailError] = useState<string | undefined>(undefined)

  // const [metaTxData, setMetaTxData] = useState<MetaTransactionData | undefined>(undefined)
  // const [fee, setFee] = useState('')
  // const [feeError, setFeeError] = useState('')

  const chainId = useChainId()

  useEffect(() => {
    if (isSending) {
      if (txStatus === PendingStatus.INDEXING) {
        onSendSuccess()
        setModalOpen(false)
      }
    }
  }, [txStatus, isSending, sendError, onSendSuccess, setModalOpen])

  const clearInputs = () => {
    setAddress('')
    setName('')
    setEmail('')
    setPercentage(0)
    // clear errors
    setAddressError(undefined)
    setNameError(undefined)
    setEmailError(undefined)
    setWarning(false)
  }

  // const handleFeeError = (error: string) => {
  //   setFeeError(error)
  // }

  // const handleFeeChange = ({ feeType, feeAmount }: { feeType: string; feeAmount: string }) => {
  //   setFee(feeAmount)
  // }

  // useEffect(() => {
  //   if (index === -1) {
  //     const txParams = encodeAddInheritorFunc(address, percentage, chainId)
  //     setMetaTxData(txParams)
  //   } else {
  //     if (inheritor && inheritor.index !== undefined && inheritor.id !== undefined) {
  //       if (inheritor.address !== address || inheritor.percentage !== percentage) {
  //         const txParams = encodeChangeInheritorFunc(chainId, inheritor.address, address, percentage)
  //         setMetaTxData(txParams)
  //         return
  //       }
  //     }
  //     setMetaTxData(undefined)
  //   }
  // }, [address, chainId, index, inheritor, percentage])

  // validate email input
  const validateEmail = (email: string) => {
    const re = /\S+@\S+\.\S+/
    return re.test(email)
  }

  // validate name input not special characters
  const validateName = (name: string) => {
    // const re = /^[a-zA-Z0-9_ ]*$/
    return name.length <= 100
  }

  const disableAddButton = (): boolean => {
    if (address !== '' && !addressError && !nameError && !emailError && percentage > 0) return false
    return true
  }

  const disableEditButton = (): boolean => {
    if (address !== '' && !addressError && !nameError && !emailError && percentage > 0) {
      if (
        inheritor?.address === address &&
        inheritor?.percentage === percentage &&
        inheritor?.name === name &&
        inheritor?.email === email
      )
        return true
      return false
    }
    return true
  }

  const handleConfirm = () => {
    if (index === -1) {
      const txParams = encodeAddInheritorFunc(address, percentage, chainId)
      void sendTx(txParams, true)
      dispatch(upsertAddressBookEntry({ chainId, address, name, email }))
    } else {
      if (inheritor.index !== undefined && inheritor.id !== undefined) {
        if (inheritor.address !== address || inheritor.percentage !== percentage) {
          const txParams = encodeChangeInheritorFunc(chainId, inheritor.address, address, percentage)
          void sendTx(txParams, true)
        }
        dispatch(upsertAddressBookEntry({ chainId, address, name, email }))
      }
    }
  }

  const handleBlur = () => {
    if (percentage > maxPercentage) {
      setWarning(true)
    } else {
      setWarning(false)
    }

    const _percentage = percentage < 1 ? 1 : percentage > maxPercentage ? maxPercentage : percentage
    setPercentage(_percentage)
  }

  const handleSliderCommitted = (event: Event | SyntheticEvent<Element, Event>, newValue: number | number[]) => {
    const newPercentage = newValue as number
    if (newPercentage > maxPercentage) {
      setWarning(true)
    } else {
      setWarning(false)
    }
    const _percentage = newPercentage < 1 ? 1 : newPercentage > maxPercentage ? maxPercentage : newPercentage
    setPercentage(_percentage)
  }

  const handleInputPercentageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value === '' ? 0 : Number(event.target.value)
    setPercentage(newValue)
  }

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    const newPercentage = newValue as number
    setPercentage(newPercentage)
  }

  const validateDuplicateInheritor = (address: string) => {
    if (index !== -1 && sameAddress(address, inheritor.address)) {
      return true
    }

    const addressFound = inheritors.filter((inheritor) => sameAddress(inheritor.address, address))
    if (addressFound.length > 0) {
      return false
    }
    return true
  }

  const handleInputChange = (id: string, value: string) => {
    // const { id, value } = event.target

    if (id === 'inheritorAddr') {
      if (value === '') {
        setAddressError('Address is required')
      } else if (!validateDuplicateInheritor(value)) {
        setAddressError('The inheritor already exists')
      } else if ((wallet && wallet.address === value) || safeAddress === value) {
        setAddressError(`Can't add yourself as an inheritor`)
      } else {
        setAddressError(validateAddress(value))
      }
      setAddress(value)
    }

    if (id === 'name') {
      if (value !== '' && !validateName(value)) {
        setNameError('Invalid name')
      } else {
        setNameError(undefined)
      }
      setName(value)
    }
    if (id === 'email') {
      if (value !== '' && !validateEmail(value)) {
        setEmailError('Invalid email')
      } else {
        setEmailError(undefined)
      }
      setEmail(value)
    }
  }

  const renderFooter = () => {
    return (
      <Grid item xs={12} mt="16px">
        <Grid container>
          <Grid item xs={5} sm={4}>
            <Button
              variant="text"
              sx={{
                paddingY: '10px',
                width: '100%',
                borderRadius: '4px',
                background: 'var(--Ebony-Clay-950, #2F354B)',
                color: 'var(--Ebony-Clay-300, #D2D7E5)',
                ':hover': {
                  background: 'rgba(255, 255, 255, 0.08)',
                },
              }}
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item xs={2} sm={4}></Grid>
          <Grid item xs={5} sm={4} textAlign="right">
            <Button
              disabled={index !== -1 ? disableEditButton() : disableAddButton()}
              variant="contained"
              sx={{
                paddingY: '10px',
                width: '100%',
              }}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <Box>
      <Grid
        key="edit_inheritor"
        container
        sx={{
          marginTop: '16px',
          marginBottom: '8px',
          padding: '16px',
          pt: '4px',
          borderRadius: '4px',
          border: '1px solid var(--Ebony-Clay-950, #2F354B)',
        }}
      >
        <Grid item xs={12} textAlign="right" mb="8px">
          {/* {inheritors.length > 1 && (
            <IconButton sx={{ p: '4px' }} onClick={() => handleDeleteInheritor(index)}>
              <Close fontSize="small" />
            </IconButton>
          )} */}
        </Grid>

        {/* {renderInput('inheritorAddr', 'Inheritor address', address, handleInputChange, addressError)} */}
        <TextInputComponent
          id="inheritorAddr"
          label="Inheritor address"
          value={address}
          onChange={handleInputChange}
          errorMessages={addressError}
          showQRButton={true}
        />
        <Grid item xs={12} mb="10px">
          <Body2White>Set inheritor percentage</Body2White>
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            sx={{
              background: 'var(--Supporting-Dark-BG-700, #272A3E)',
              borderRadius: '4px',
            }}
            px={isDesktop ? '16px' : '8px'}
            py="8px"
          >
            <Grid item xs="auto">
              <Input
                // autoFocus={isCoinbase}
                sx={{
                  background: 'var(--Ebony-Clay-900, #343C58)',
                  borderRadius: '4px',
                  py: '6px',
                  px: isDesktop ? '12px' : '4px',
                  width: 'auto',
                  marginRight: isDesktop ? '16px' : '8px',
                }}
                disableUnderline
                value={Number(percentage).toString()}
                size="small"
                onChange={handleInputPercentageChange}
                onBlur={handleBlur}
                inputProps={{
                  style: { textAlign: 'center', padding: 0 },
                  step: 1,
                  min: 0,
                  max: 100,
                  type: 'number',
                  'aria-labelledby': 'input-slider',
                }}
                endAdornment={<Body2White>%</Body2White>}
              />
            </Grid>
            <Grid item container alignItems="center" justifyContent="center" xs>
              {isCoinbase && (
                <Body2White sx={{ color: 'var(--Ebony-Clay-600, #5B6B9C)', fontSize: '14px' }}>
                  Type percentage in the left box
                </Body2White>
              )}
              {!isCoinbase && (
                <PrettoSlider
                  value={typeof percentage === 'number' ? percentage : 1}
                  onChange={handleSliderChange}
                  onChangeCommitted={handleSliderCommitted}
                  aria-labelledby="input-slider"
                />
              )}
            </Grid>
          </Grid>
          {warning && (
            <Body2White sx={{ color: 'var(--Supporting-Koromiko, #FC8313)' }}>
              Only {maxPercentage}% of the inheritance can be allocated.
            </Body2White>
          )}
        </Grid>
        {/* {renderInput('name', 'Inheritor name (optional)', name, handleInputChange, nameError)}
        {renderInput('email', 'Inheritor email (optional)', email, handleInputChange, emailError)} */}
        <TextInputComponent
          id="name"
          label="Inheritor name (optional)"
          value={name}
          onChange={handleInputChange}
          errorMessages={nameError}
        />
        <TextInputComponent
          id="email"
          label="Inheritor email (optional)"
          value={email}
          onChange={handleInputChange}
          errorMessages={emailError}
        />
      </Grid>
      {sendError !== '' && (
        <Body2White sx={{ color: 'var(--Supporting-Carnation, #FF2239)', marginTop: '8px' }}>{sendError}</Body2White>
      )}
      {/* <FeeSection metaTxData={metaTxData} onError={handleFeeError} onFee={handleFeeChange} /> */}
      {renderFooter()}
    </Box>
  )
}

export default EditInheritorComponent
